<template>
  <b-modal
    id="quick-create-weight-modal"
    ref="quickCreateWeightModal"
    title="Créer un nouveau poids volumétrique"
    size="md"
    hide-footer
    centered
    modal-class="modal-primary"
    @hidden="resetForm"
  >
    <validation-observer ref="simpleRules">
      <b-form @submit.prevent="submitWeight">
        <b-row>
          <!-- Nom du poids -->
          <b-col cols="12">
            <b-form-group
              label="Nom du poids volumétrique *"
              label-for="quick-weight-name"
              description="Ex: 0 - 5 kg, 5 - 10 kg, Standard"
            >
              <validation-provider
                #default="{ errors }"
                name="Nom du poids"
                rules="required"
              >
                <b-form-input
                  id="quick-weight-name"
                  v-model="weight.weight"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Ex: 0 - 5 kg"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Unité -->
          <b-col md="6">
            <b-form-group
              label="Unité de mesure *"
              label-for="quick-weight-unit"
            >
              <validation-provider
                #default="{ errors }"
                name="Unité"
                rules="required"
              >
                <b-form-input
                  id="quick-weight-unit"
                  v-model="weight.unit"
                  :state="errors.length > 0 ? false : null"
                  placeholder="kg, g, etc."
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Statut -->
          <b-col
            md="6"
            class="d-flex align-items-center mt-1"
          >
            <b-form-group class="mb-0">
              <b-form-checkbox
                id="quick-weight-is-active"
                v-model="weight.is_active"
                name="is_active"
                switch
                class="custom-control-success"
              >
                <span class="font-weight-bold">{{ weight.is_active ? 'Actif' : 'Inactif' }}</span>
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <!-- Min -->
          <b-col md="6">
            <b-form-group
              label="Poids minimum *"
              label-for="quick-weight-min"
            >
              <validation-provider
                #default="{ errors }"
                name="Poids minimum"
                rules="required"
              >
                <b-form-input
                  id="quick-weight-min"
                  v-model="weight.min"
                  type="text"
                  inputmode="decimal"
                  :formatter="formatPrice"
                  :state="errors.length > 0 ? false : null"
                  placeholder="0.000"
                  @keypress="allowOnlyDecimalNumbers"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Max -->
          <b-col md="6">
            <b-form-group
              label="Poids maximum *"
              label-for="quick-weight-max"
            >
              <validation-provider
                #default="{ errors }"
                name="Poids maximum"
                rules="required"
              >
                <b-form-input
                  id="quick-weight-max"
                  v-model="weight.max"
                  type="text"
                  inputmode="decimal"
                  :formatter="formatPrice"
                  :state="errors.length > 0 ? false : null"
                  placeholder="5.000"
                  @keypress="allowOnlyDecimalNumbers"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Description -->
          <b-col cols="12">
            <b-form-group
              label="Description"
              label-for="quick-weight-description"
            >
              <b-form-textarea
                id="quick-weight-description"
                v-model="weight.description"
                rows="2"
                placeholder="Description optionnelle..."
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            :disabled="isLoading"
            @click="hide"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="isLoading"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-1"
            />
            <span>{{ isLoading ? 'Création...' : 'Créer et appliquer' }}</span>
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BRow,
  BCol,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'QuickCreateWeightModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      weight: {
        weight: '',
        min: '',
        max: '',
        unit: 'kg',
        description: '',
        is_active: true,
      },
      isLoading: false,
    }
  },
  methods: {
    show() {
      this.$refs.quickCreateWeightModal.show()
    },
    hide() {
      this.$refs.quickCreateWeightModal.hide()
    },
    resetForm() {
      this.weight = {
        weight: '',
        min: '',
        max: '',
        unit: 'kg',
        description: '',
        is_active: true,
      }
      this.isLoading = false
    },
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async submitWeight() {
      const isValid = await this.$refs.simpleRules.validate()
      if (!isValid) return

      this.isLoading = true
      try {
        const payload = {
          ...this.weight,
          min: this.weight.min ? parseFloat(this.weight.min) : null,
          max: this.weight.max ? parseFloat(this.weight.max) : null,
        }

        const { data } = await axios.post('/api/weights/create/', payload)

        this.showToast('success', 'Poids créé', `Le poids "${data.weight}" a été créé avec succès.`)
        this.$emit('weight-created', data)
        this.hide()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating weight:', error)
        let errorMsg = 'Erreur lors de la création du poids'
        if (error.response && error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMsg = error.response.data
          } else if (error.response.data.detail) {
            errorMsg = error.response.data.detail
          } else if (typeof error.response.data === 'object') {
            const msgs = Object.values(error.response.data).flat()
            if (msgs.length > 0) errorMsg = msgs.join(' ')
          }
        }
        this.showToast('danger', 'Erreur', errorMsg)
      } finally {
        this.isLoading = false
      }
    },
    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          variant,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
        },
      })
    },
  },
}
</script>
